import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Send, Mic, Square, Check, RotateCcw, ArrowRight } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CommandBox() {
  const { shopId } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // interpret result
  const [done, setDone] = useState(null);        // execute result (with undo)
  const [error, setError] = useState("");
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const reset = () => { setResult(null); setDone(null); setError(""); };

  const interpret = async (t) => {
    const say = (t ?? text).trim();
    if (!say) return;
    setBusy(true); reset();
    try {
      const r = await api.post("/assistant/interpret", { shopId, text: say });
      setResult(r.data);
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post("/assistant/execute", { shopId, action: result.action });
      setDone(r.data); setResult(null); setText("");
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); }
  };

  const doUndo = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post("/assistant/undo", { shopId, undo: done.undo });
      setDone({ message: r.data.message, undone: true });
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); }
  };

  // voice
  const startRec = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const form = new FormData(); form.append("file", blob, "cmd.webm");
        setBusy(true); reset();
        try {
          const r = await fetch(`${BACKEND}/api/assistant/voice?shopId=${shopId}`, { method: "POST", body: form, credentials: "include" });
          setResult(await r.json());
        } catch { setError("We couldn't read that voice note."); }
        finally { setBusy(false); }
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch { setError("We couldn't use the microphone. Type instead."); }
  };
  const stopRec = () => { setRecording(false); mediaRef.current?.stop(); };

  if (!open) {
    return (
      <button className="cmd-fab" data-testid="command-open" onClick={() => setOpen(true)} aria-label="Ask or tell Reto">
        <Sparkles size={20} strokeWidth={2} />
      </button>
    );
  }

  return (
    <div className="cmd-panel" data-testid="command-box" role="dialog" aria-label="Command box">
      <div className="cmd-head">
        <span className="row" style={{ gap: 8 }}><Sparkles size={16} /> <strong>Tell Reto</strong></span>
        <button className="btn btn-ghost btn-sm" data-testid="command-close" onClick={() => { setOpen(false); reset(); }}><X size={18} /></button>
      </div>

      <div className="cmd-inputrow">
        <input className="input" data-testid="command-input" value={text} autoFocus
          placeholder="e.g. add halo-halo 120"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && interpret()} />
        {!recording ? (
          <button className="btn btn-sm" data-testid="command-mic" onClick={startRec} aria-label="Speak"><Mic size={18} /></button>
        ) : (
          <button className="btn btn-sm btn-danger" data-testid="command-mic-stop" onClick={stopRec} aria-label="Stop"><Square size={16} /></button>
        )}
        <button className="btn btn-primary btn-sm" data-testid="command-send" onClick={() => interpret()} disabled={busy || !text.trim()}>
          <Send size={16} />
        </button>
      </div>
      <p className="muted-3 t-small" style={{ margin: "4px 2px 0" }}>Taglish is fine. Nothing happens until you say Yes.</p>

      {error ? <div className="err-text t-small" data-testid="command-error" style={{ marginTop: 10 }}>{error}</div> : null}
      {busy ? <p className="muted t-small" style={{ marginTop: 10 }}>thinking…</p> : null}

      {/* ANSWER */}
      {result?.kind === "answer" ? (
        <div className="cmd-card" data-testid="command-answer">{result.text}</div>
      ) : null}

      {/* CLARIFY */}
      {result?.kind === "clarify" ? (
        <div className="cmd-card" data-testid="command-question">
          <div>{result.question}</div>
          {(result.options || []).length ? (
            <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {result.options.map((o, i) => (
                <button key={i} className="btn btn-sm" data-testid={`command-option-${i}`} onClick={() => { setText(o.text); interpret(o.text); }}>{o.label}</button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* PROPOSE */}
      {result?.kind === "propose" ? (
        <div className="cmd-card" data-testid="command-preview">
          {(result.preview || []).map((line, i) => <div key={i} className="t-body">{line}</div>)}
          <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button className="btn btn-primary btn-sm" data-testid="command-yes" onClick={confirm} disabled={busy}>
              <Check size={16} /> {result.confirmLabel || "Yes"}
            </button>
            <button className="btn btn-sm" data-testid="command-cancel" onClick={reset}>Cancel</button>
            {result.editHref ? (
              <button className="btn btn-ghost btn-sm" data-testid="command-edit-link"
                onClick={() => { navigate(result.editHref); setOpen(false); reset(); }}>
                or do it on the screen <ArrowRight size={14} />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* DONE + UNDO */}
      {done ? (
        <div className="cmd-card cmd-done" data-testid="command-done">
          <div className="row" style={{ gap: 8 }}><Check size={16} style={{ color: "var(--good)" }} /> {done.message}</div>
          {!done.undone && done.undo ? (
            <button className="btn btn-sm" data-testid="command-undo" onClick={doUndo} style={{ marginTop: 10 }}>
              <RotateCcw size={14} /> {done.undoLabel || "Undo"}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
